import { AnimatedHeight } from '@codezo/offer-extras';
import { Card, MicroLabel, SectionHead, cn } from '@codezo/quote-detail';
import { Box, ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';

/** Specifications on the face of the card before the rest fold away. Four is
 * what fits on one line of the spec grid at the width the middle column takes. */
const FACE_SPECS = 4;

/** Thumbnails beside the hero image — one row of three, the grid it sits in. */
const FACE_THUMBS = 3;

/** One label/value pair in the order-line column or the spec grid. */
export type OrderProductFact = readonly [label: string, value: ReactNode];

export interface OrderProductRow {
	id: string;
	name: string;
	sku?: string | null;
	/**
	 * The pictures, hero first. Each one is the app's own image element — this
	 * package renders no assets — and the frame around them is drawn here so a
	 * product with none still occupies the space its neighbours do.
	 */
	media?: ReactNode[];
	/** Opens the app's image viewer at that index. Without it the pictures don't click. */
	onOpenImages?: (index: number) => void;
	description?: string | null;
	/** Frozen at acceptance — what was agreed, not what the catalogue says now. */
	specs?: OrderProductFact[];
	/** The quiet line under the specs — when they were frozen. */
	note?: ReactNode;
	/** Tech packs and artwork, as a `FileGrid` of `FileTile`s. A full-width footer. */
	files?: ReactNode;
	/** What to say when there are none. Rendered in the same footer. */
	filesEmpty?: ReactNode;
	/**
	 * The money column. Admin passes base, margin and unit; the client app passes
	 * the subset it is allowed to see. The module never decides — a row that
	 * shouldn't be shown is a row that isn't passed.
	 */
	facts?: OrderProductFact[];
	/** The bottom line, ruled off from the facts above it. */
	total?: OrderProductFact;
	/* A `badge` slot lived here, and the admin page filled it with the item's
	 * status. It contradicted the whole point of this section, stated two comments
	 * below: the flow spine owns every status, and a card that also carries one is
	 * a second answer to "where has this got to" — from a card that is explicitly
	 * not asking. Worse, the two could disagree: the spine reads the lifecycle,
	 * the pill read `OrderItemStatus`, and PARTIALLY_SHIPPED is one word for a
	 * product that is in two places at once. Removed rather than left empty, so
	 * nothing invites it back. */
	/** The row menu, riding the "Order line" label. */
	actions?: ReactNode;
	/** Pinned to the bottom of the money column. */
	primaryAction?: ReactNode;
}

/**
 * The picture well. One frame for the hero and the thumbs, so a product with no
 * image still occupies the space its neighbours do and the column of cards keeps
 * its left edge.
 *
 * A button only when the app gave us somewhere to go: the images open in the
 * app's own viewer, and without one the picture is just a picture.
 */
function MediaFrame({
	children,
	square,
	onOpen,
	title,
}: {
	children?: ReactNode;
	square?: boolean;
	onOpen?: () => void;
	title?: string;
}) {
	const frame = cn(
		'flex w-full items-center justify-center overflow-hidden border border-line-2 bg-surface-fill text-text-4',
		square ? 'aspect-square rounded-[var(--r-1)]' : 'aspect-[4/3] rounded-[var(--r-2)]'
	);
	const body = children ?? <Box size={square ? 12 : 18} />;

	if (!onOpen) return <span className={frame}>{body}</span>;
	return (
		<button
			type="button"
			title={title}
			onClick={onOpen}
			className={cn(frame, 'p-0 transition-opacity hover:opacity-80')}
		>
			{body}
		</button>
	);
}

/**
 * What is being made, as reference.
 *
 * Deliberately NOT where the work happens — the flow spine above owns every
 * action and every status. This section answers a different question: what did
 * we agree to make, at what spec, for how much. So it stays readable when an
 * item is mid-production and says nothing about where it has got to.
 *
 * ── Why it looks like the quote's requested-products card ──
 *
 * Because it is the same object being read for the same reason. On the quote you
 * check that what we are about to price is the thing the client meant; on the
 * order you check that what we are about to MAKE is the thing they bought. Both
 * need the dossier open — the picture, the material, the print, the tech pack —
 * so the two cards are deliberately the same card, down to the media frame, the
 * spec grid and the files footer.
 *
 * They are not the same COMPONENT, because the third column genuinely differs:
 * the quote's asks for quantities and a deadline, the order's states what the
 * line is worth. Everything either side of that column is duplicated structure,
 * and it is worth lifting a shared dossier shell into `quote-detail` once both
 * pages have settled — doing it now would mean refactoring a component the live
 * quote page renders.
 */
export function OrderProducts({
	products,
	meta = 'what was agreed at acceptance — specifications are frozen',
	action,
	emptyState,
}: {
	products: OrderProductRow[];
	meta?: ReactNode;
	action?: ReactNode;
	emptyState?: ReactNode;
}) {
	return (
		<section>
			<SectionHead title="Products" meta={meta} action={action} />
			{products.length === 0 ? (
				<Card>{emptyState}</Card>
			) : (
				<div className="flex flex-col gap-3">
					{products.map((product) => (
						<OrderProductCard key={product.id} product={product} />
					))}
				</div>
			)}
		</section>
	);
}

export function OrderProductCard({ product }: { product: OrderProductRow }) {
	const [expanded, setExpanded] = useState(false);

	const media = product.media ?? [];
	const thumbs = media.slice(1, 1 + FACE_THUMBS);
	const specs = product.specs ?? [];
	const shown = expanded ? specs : specs.slice(0, FACE_SPECS);
	const hasTerms = !!(product.facts?.length || product.total || product.primaryAction);

	return (
		<Card>
			<div className="flex flex-wrap gap-5 p-4">
				<div className="flex min-w-[150px] flex-[0_1_200px] flex-col gap-1.5">
					<MediaFrame
						onOpen={product.onOpenImages ? () => product.onOpenImages?.(0) : undefined}
						title={`View images of ${product.name}`}
					>
						{media[0]}
					</MediaFrame>
					{thumbs.length > 0 && (
						<div className="grid grid-cols-3 gap-1.5">
							{thumbs.map((thumb, i) => (
								<MediaFrame
									key={i}
									square
									onOpen={
										product.onOpenImages
											? () => product.onOpenImages?.(i + 1)
											: undefined
									}
									title={`View images of ${product.name}`}
								>
									{thumb}
								</MediaFrame>
							))}
						</div>
					)}
				</div>

				<div className="flex min-w-[260px] flex-[2_1_300px] flex-col gap-2.5">
					<div>
						<div className="flex flex-wrap items-baseline gap-2.5">
							<span className="text-base font-medium text-text-1">
								{product.name}
							</span>
							{product.sku && (
								<span className="font-mono text-xs text-text-4">{product.sku}</span>
							)}
						</div>
						{product.description && (
							<div className="mt-1.5 max-w-[520px] text-sm leading-relaxed text-text-3">
								{product.description}
							</div>
						)}
					</div>

					{specs.length > 0 && (
						<div className="flex flex-col gap-2">
							{/* Measured rather than switched: the tail unfolds into the same
							    grid, so the four specifications already on the card don't
							    jump as the rest arrive. */}
							<AnimatedHeight step={expanded ? 'all' : 'face'}>
								<div className="grid gap-x-6 gap-y-1.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
									{shown.map(([label, value], i) => (
										<div
											key={`${label}-${i}`}
											className="flex min-w-0 flex-col"
										>
											<span className="text-2xs text-text-4">{label}</span>
											{/* Two lines while the list is short, all of them
											    once the reader has asked for all of them —
											    which they did in order to read them. */}
											<span
												className={cn(
													'min-w-0 text-sm break-words text-text-2',
													!expanded && 'line-clamp-2'
												)}
											>
												{value}
											</span>
										</div>
									))}
								</div>
							</AnimatedHeight>

							{specs.length > FACE_SPECS && (
								<button
									type="button"
									onClick={() => setExpanded((v) => !v)}
									aria-expanded={expanded}
									className="flex w-fit items-center gap-1 bg-transparent p-0 text-xs text-text-4 transition-colors hover:text-text-1"
								>
									<ChevronDown
										size={12}
										className={cn(
											'transition-transform duration-150',
											expanded && 'rotate-180'
										)}
									/>
									{expanded
										? 'Fewer specifications'
										: `All ${specs.length} specifications`}
								</button>
							)}
						</div>
					)}

					{product.note && (
						<div className="text-2xs leading-normal text-text-4">{product.note}</div>
					)}
				</div>

				{hasTerms && (
					<div className="flex min-w-[170px] flex-[1_0_180px] flex-col gap-3 border-l border-line-1 pl-5">
						<div>
							{/* The row menu rides the label rather than floating over the
							    card: this column is where the line is edited, and a menu
							    with nothing beside it reads as belonging to the section. */}
							<div
								className={cn(
									'mb-1.5 flex items-center justify-between gap-2',
									product.actions && 'min-h-[28px]'
								)}
							>
								<MicroLabel>Order line</MicroLabel>
								{product.actions && (
									<span className="shrink-0">{product.actions}</span>
								)}
							</div>
							<div className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1">
								{(product.facts ?? []).map(([label, value], i) => (
									<Fact key={`${label}-${i}`} label={label} value={value} />
								))}
							</div>
							{product.total && (
								<div className="mt-2 flex items-baseline justify-between gap-2.5 border-t border-line-1 pt-2">
									<span className="text-2xs text-text-4">{product.total[0]}</span>
									<span className="font-mono text-base font-medium text-text-1">
										{product.total[1]}
									</span>
								</div>
							)}
						</div>

						{product.primaryAction && (
							<div className="mt-auto pt-1">{product.primaryAction}</div>
						)}
					</div>
				)}
			</div>

			{/* Full width under the columns. Files are tiles, and a tile grid in the
			    middle column would be three tiles wide with the description clipped
			    in each — which is the half of a file that says what it is for. */}
			{(product.files || product.filesEmpty) && (
				<div className="border-t border-line-1 px-4 py-3.5">
					{product.files ?? (
						<span className="text-sm text-text-4">{product.filesEmpty}</span>
					)}
				</div>
			)}
		</Card>
	);
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
	return (
		<>
			<span className="text-2xs text-text-4">{label}</span>
			<span className="text-right font-mono text-xs text-text-2">{value}</span>
		</>
	);
}
